import { CELL } from '../core/config';
import type { Building, Cell, CellBounds, OccupiedPart } from './city';

/** Large visible pieces are the same occupied parts used by physics. The
 * furniture item retains one stable damage identity and its real floor parent. */
export function furnish(
  cell: Cell,
  kind: NonNullable<Building['furniture']>,
  variant = 0,
  bedAlongRoom = false,
): void {
  const parts: OccupiedPart[] = [],
    wood = [0xb39979, 0x9d7d5e, 0xc1ae8d][variant % 3],
    cloth = [0x9fae9e, 0xb2a08c, 0x8d9da0, 0xb4ad98][variant % 4],
    steel = 0x61716d;
  const box = (x: number, y: number, z: number, w: number, h: number, d: number, color = wood) =>
    parts.push({ x: x / CELL, y: y / CELL, z: z / CELL, w: w / CELL, h: h / CELL, d: d / CELL, color });
  const legs = (x: number, z: number, w: number, d: number, height: number, size = 0.065) => {
    for (const dx of [x, x + w - size])
      for (const dz of [z, z + d - size]) box(dx, 0, dz, size, height, size, steel);
  };
  if (kind === 'bench') {
    legs(0.13, 0.2, 1.07, 0.76, 0.42, 0.085);
    for (const z of [0.2, 0.37, 0.54, 0.71]) box(0.07, 0.42, z, 1.11, 0.07, 0.13);
    for (const x of [0.13, 1.03]) box(x, 0.35, 0.87, 0.085, 0.58, 0.085, steel);
    for (const y of [0.64, 0.82]) box(0.07, y, 0.9, 1.11, 0.12, 0.055);
  } else if (kind === 'chair') {
    legs(0.31, 0.3, 0.63, 0.66, 0.42);
    box(0.28, 0.42, 0.27, 0.69, 0.07, 0.72, cloth);
    box(0.31, 0.49, 0.89, 0.63, 0.45, 0.08, cloth);
  } else if (kind === 'sofa') {
    legs(0.11, 0.12, 1.03, 1.01, 0.2, 0.1);
    box(0.1, 0.2, 0.1, 1.05, 0.18, 1.05);
    box(0.23, 0.38, 0.13, 0.79, 0.1, 0.84, cloth);
    box(0.1, 0.38, 0.98, 1.05, 0.48, 0.17, cloth);
    for (const x of [0.1, 1.02]) box(x, 0.38, 0.1, 0.13, 0.28, 0.88, cloth);
  } else if (kind === 'bed') {
    legs(0.12, 0.09, 1.01, 1.88, 0.23, 0.09);
    box(0.1, 0.23, 0.07, 1.05, 0.12, 1.95);
    box(0.13, 0.35, 0.1, 0.99, 0.13, 1.89, cloth);
    box(0.2, 0.48, 0.15, 0.85, 0.085, 0.34, 0xe7e0ce);
    box(0.1, 0.35, 0.02, 1.05, 0.47, 0.07);
  } else if (kind === 'shelf') {
    for (const x of [0.1, 1.07]) box(x, 0, 0.1, 0.08, 1.65, 0.98);
    box(0.18, 0, 1.01, 0.89, 1.65, 0.07);
    for (let row = 0; row < 4; row++) {
      box(0.18, row * 0.4, 0.1, 0.89, 0.06, 0.91);
      for (let k = 0; k < 3; k++)
        box(
          0.23 + k * 0.27,
          row * 0.4 + 0.06,
          0.29,
          0.18,
          0.22 + ((variant + k) % 2) * 0.08,
          0.35,
          [0x8a9e87, 0xd0ad79, 0xc39a83][(k + variant) % 3],
        );
    }
  } else if (kind === 'cabinet') {
    box(0.11, 0, 0.24, 1.03, 1.88, 0.78);
    for (const x of [0.13, 0.64]) box(x, 0.07, 0.2, 0.48, 1.74, 0.04, cloth);
    for (const x of [0.55, 0.68]) box(x, 0.86, 0.17, 0.03, 0.16, 0.03, steel);
  } else if (kind === 'kitchen') {
    box(0.1, 0, 0.16, 1.05, 0.85, 0.91, 0x9ca698);
    box(0.08, 0.85, 0.12, 1.09, 0.06, 0.97, 0xd8d0b9);
    box(0.1, 0.91, 1.02, 1.05, 0.38, 0.07, 0xcac9b5);
    box(0.21, 0.91, 0.35, 0.36, 0.025, 0.45, steel);
    box(0.76, 0.91, 0.59, 0.19, 0.24, 0.2, 0x646f68);
    box(0.77, 1.15, 0.61, 0.17, 0.035, 0.16, steel);
    box(0.62, 0.91, 0.24, 0.34, 0.035, 0.18, wood);
  } else if (kind === 'crate') {
    box(0.12, 0, 0.12, 1.01, 0.68, 1.01, wood);
    box(0.17, 0.68, 0.17, 0.91, 0.025, 0.91, 0xa58c6c);
  } else {
    const counter = kind === 'counter',
      height = counter ? 0.91 : 0.76;
    legs(0.12, 0.12, 1.01, 1.01, height - 0.07);
    box(0.1, height - 0.07, 0.1, 1.05, 0.07, 1.05);
    if (kind === 'desk') {
      box(0.5, height, 0.82, 0.2, 0.1, 0.16, steel);
      box(0.31, height + 0.1, 0.85, 0.61, 0.31, 0.055, 0x294a51);
      box(0.36, height, 0.3, 0.53, 0.025, 0.2, 0x66797b);
    } else if (counter) {
      box(0.15, 0.12, 0.21, 0.95, 0.56, 0.73, 0x8e9c96);
      box(0.76, height, 0.73, 0.27, 0.26, 0.12, 0x344f55);
    } else {
      box(0.22, height, 0.62, 0.22, 0.19, 0.25, steel);
      box(0.65, height, 0.37, 0.32, 0.065, 0.38, 0xb9a987);
    }
  }
  // Beds run across the room, leaving the original front-to-back aisles clear.
  if (kind === 'bed' && !bedAlongRoom)
    for (const p of parts) {
      [p.x, p.z] = [p.z, p.x];
      [p.w, p.d] = [p.d, p.w];
    }
  const bounds: CellBounds = { x: Infinity, y: Infinity, z: Infinity, w: 0, h: 0, d: 0 };
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  for (const p of parts) {
    bounds.x = Math.min(bounds.x, p.x);
    bounds.y = Math.min(bounds.y, p.y);
    bounds.z = Math.min(bounds.z, p.z);
    maxX = Math.max(maxX, p.x + p.w);
    maxY = Math.max(maxY, p.y + p.h);
    maxZ = Math.max(maxZ, p.z + p.d);
  }
  bounds.w = maxX - bounds.x;
  bounds.h = maxY - bounds.y;
  bounds.d = maxZ - bounds.z;
  cell.bounds = bounds;
  cell.parts = parts;
}
